import React, { useEffect, useRef, useState } from 'react'
import Layout from '../layout/Layout'
import Spinner from "react-bootstrap/Spinner";
import CustomFormInput from '../components/CustomFormInput';
import UserActivitiesModal from '../components/UserActivitiesModal';
import Loader from '../components/Loader';
import { getActivityImages } from '../controller';
import { requestHandler } from '../helper';
import { imagePath } from '../helper/image';

const PAGE_SIZE = 20;

const fullUrl = (p) => (p ? (/^https?:\/\//.test(p) ? p : `${imagePath()}${p.replace(/^\/+/, '')}`) : '');
const uploaderName = (u) => [u?.firstName, u?.lastName].filter(Boolean).join(' ') || u?.userName || 'Deleted user';

function ActivityImages() {
  const [images, setImages] = useState([]);
  const [totalData, setTotalData] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const requestId = useRef(0);
  const sentinelRef = useRef(null);
  const hasMore = images.length < totalData;

  // reset=true starts the list over (first load / new search); otherwise appends the next batch
  const loadImages = async (reset = false) => {
    const id = ++requestId.current;
    const loaded = reset ? [] : images;
    // derive the page from what's loaded so deletions never leave a gap (overlap is deduped below)
    const page = Math.floor(loaded.length / PAGE_SIZE) + 1;
    await requestHandler(
      async () => await getActivityImages(PAGE_SIZE, page, search),
      reset ? setLoading : setLoadingMore,
      (res) => {
        if (id !== requestId.current) return; // a newer search replaced this request
        const { data = [], totalData } = res.data;
        const seen = new Set(loaded.map((img) => img._id));
        const added = data.filter((img) => !seen.has(img._id));
        setImages([...loaded, ...added]);
        setTotalData(added.length ? totalData : loaded.length);
      }
    );
  };

  useEffect(() => {
    const t = setTimeout(() => loadImages(true), search ? 500 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  // load the next batch when the bottom of the grid scrolls into view
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore || loading || loadingMore) return;
    const observer = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && loadImages(),
      { rootMargin: '300px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line
  }, [images, hasMore, loading, loadingMore]);

  // the whole activity post was deleted: drop all of its tiles
  const handleDeleted = (activityId) => {
    const removed = images.filter((img) => img.activityId === activityId).length;
    setImages((prev) => prev.filter((img) => img.activityId !== activityId));
    setTotalData((prev) => Math.max(prev - removed, 0));
  };

  return (
    <>
      {loading && <Loader />}
      <Layout>
        <div className="card-wrapper">
          <div className="card-header border-bottom-0 pb-0">
            <div className="search-input">
              <img src="/images/svg/search.svg" className="search-icon" alt="icon" />
              <CustomFormInput
                type="search"
                placeholder="Search by user or caption..."
                id="search"
                value={search}
                name='search'
                onChange={(e) => setSearch(e.target.value)}
                className="mb-0"
              />
            </div>
          </div>
          <div className="card-body">
            {images.length ? (
              <div className="image-grid">
                {images.map((img) => (
                  <button type="button" key={img._id} className="image-tile" onClick={() => setSelected(img)} title="View details">
                    <img src={fullUrl(img.image?.thumbnail || img.image?.url)} alt={img.image?.originalName || 'Activity'} loading="lazy" />
                    <span className="tile-meta">{uploaderName(img.user)}</span>
                  </button>
                ))}
              </div>
            ) : (
              !loading && <div className='text-center p-5 fs-5'>No Images Found</div>
            )}
            <div ref={sentinelRef} />
            <div className="d-flex align-items-center justify-content-center gap-2 mt-3">
              {loadingMore ? (
                <Spinner animation="border" size="sm" />
              ) : (
                images.length > 0 && (
                  <p className="mb-0 fs-16 fw-500 text-light">
                    Showing {images.length} of {totalData} images
                  </p>
                )
              )}
            </div>
          </div>
        </div>
      </Layout>

      <UserActivitiesModal
        show={!!selected}
        onClose={() => setSelected(null)}
        activities={selected ? [{
          _id: selected.activityId,
          activity_image: selected.activity_image,
          des: selected.des,
          createdAt: selected.uploadedAt,
          isPrivate: selected.isPrivate,
        }] : []}
        user={selected?.user}
        startMediaId={selected?.image?._id}
        onDeleted={handleDeleted}
      />
    </>
  )
}

export default ActivityImages
